import React from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  useParams,
} from "react-router-dom";
import MainLayout from "./layout/MainLayout";
import Home from "./pages/Home";
import "./App.css";
import Project from "./pages/Project";
import LoginPage from "./pages/LoginPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import Initiative from "./pages/Initiative";
import Preview from "./pages/Preview";
import NotFound from "./pages/NotFound";
import VerifyEmail from "./pages/VerifyEmail";
import UserExtraDetails from "./pages/UserExtraDetails";
import SupplementaryMaterials from "./pages/SupplementaryMaterials";
import Notifications from "./pages/Notifications";
import UserProfile from "./pages/UserProfile";
import SecondaryLayout from "./layout/SecondaryLayout";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Organization from "./pages/Organization";
import Risk from "./pages/Risk";
import Lesson from "./pages/Lesson";
import FieldSettings from "./components/FieldSettings";
import Alert from "./pages/Alert";
import PreviousUploads from "./pages/PreviousUploads";
import InsightsAndPostchecks from "./pages/InsightsAndPostchecks";
import Hotjar from "./components/tracking/Hotjar";
import EditUserProfile from "./pages/UpdateUserProfile";
import ExploreResearchPapers from "./pages/ExploreResearchPapers";
import LearningHub from "./pages/LearningHub";
import RemindersPage from "./pages/RemindersPage";

// import context providers
import InitiativeContextProvider from "./contexts/InitiativeContextProvider";
import UserContextProvider from "./contexts/UserContextProvider";
import PaperContextProvider from "./contexts/PaperContextProvider";
import OrganizationContextProvider from "./contexts/OrganizationContextProvider";
import NotificationContextProvider from "./contexts/NotificationContextProvider";
import JournalRecommender from "./pages/researchSpotLight/JournalRecommender";
import ConferenceRecommender from "./pages/researchSpotLight/ConferenceRecommnder";
import ManuscriptValidator from "./pages/researchSpotLight/ManuscriptValidator";
import ResearchSpotLight from "./pages/researchSpotLight/ResearchSpotLight";

// wrapper components to provide initiative and paper context
function InitiativeWrapper({ children }) {
  const { projectId } = useParams();
  return (
    <InitiativeContextProvider projectId={projectId}>
      {children}
    </InitiativeContextProvider>
  );
}

// wrapper component to provide paper context
function PaperWrapper({ children }) {
  const { paperId } = useParams();
  return (
    <PaperContextProvider paperId={paperId}>{children}</PaperContextProvider>
  );
}

const App = () => {
  return (
    <UserContextProvider>
      <NotificationContextProvider>
        <OrganizationContextProvider>
          <Router>
            <Hotjar />

            <Routes>
              <Route path="/" element={<LoginPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/verifyEmail" element={<VerifyEmail />} />
              <Route path="/extra/details" element={<UserExtraDetails />} />

              {/*Main layout contains upper navigation bar and the left side bar*/}
              <Route element={<MainLayout />}>
                <Route index path="/home" element={<Home />} />
                <Route
                  path="/project/:projectId"
                  element={
                    <InitiativeWrapper>
                      <Project />
                    </InitiativeWrapper>
                  }
                />
                <Route
                  path="/project/:projectId/initiative/:initiativeId"
                  element={
                    <InitiativeWrapper>
                      <Initiative />
                    </InitiativeWrapper>
                  }
                />
                <Route
                  path="/paper/:paperId"
                  element={
                    <PaperWrapper>
                      <SupplementaryMaterials />
                    </PaperWrapper>
                  }
                />
                <Route
                  path="/research-spotlight"
                  element={<ResearchSpotLight />}
                />
                <Route path="/learning-hub" element={<LearningHub />} />
                <Route path="/risk" element={<Risk />} />
                <Route path="/lesson" element={<Lesson />} />
                <Route
                  path="/project/goals/settings/:projectId"
                  element={<FieldSettings />}
                />

                <Route
                  path="/journal-recommender"
                  element={<JournalRecommender />}
                />

                <Route
                  path="/conference-recommender"
                  element={<ConferenceRecommender />}
                />

                <Route path="/reminders" element={<RemindersPage />} />
                

                <Route path="/alert" element={<Alert />} />

                <Route path="/user/notification" element={<Notifications />} />
                <Route path="/organization" element={<Organization />} />
                <Route
                  path="/manuscript-validator"
                  element={<ManuscriptValidator />}
                />
                <Route
                  path="/journals/:journalId/preview/:paperId"
                  element={
                    <PaperWrapper>
                      <Preview />
                    </PaperWrapper>
                  }
                />
                <Route path="/previous-uploads" element={<PreviousUploads />} />
                <Route
                  path="/insights-postchecks/:paperId"
                  element={
                    <PaperWrapper>
                      <InsightsAndPostchecks />
                    </PaperWrapper>
                  }
                />
                <Route path="/edit-profile" element={<EditUserProfile />} />
                <Route
                  path="/explore-research-papers"
                  element={<ExploreResearchPapers />}
                />
              </Route>

              {/* layout with upper navigation */}
              <Route element={<SecondaryLayout />}>
                <Route path="/userProfile" element={<UserProfile />} />
              </Route>

              {/* 404 Route */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Router>

          <div>
            <ToastContainer />
          </div>
        </OrganizationContextProvider>
      </NotificationContextProvider>
    </UserContextProvider>
  );
};

export default App;
